import { del, get, post, put } from '@api/api';
import API_ROUTES from '@api/routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ITenantCreationFields } from 'types/settings';
import { ITenant, ITenantCreateResponse, ITenants } from 'types/tenants';
import QueryKeys from '.';

const getTenants = async () => {
	const res = await get<ITenants>(API_ROUTES.v1.tenants.all);
	return res.data;
};

export const addTenant = async (tenant: ITenantCreationFields) => {
	const res = await post<ITenantCreationFields, ITenantCreateResponse>(
		API_ROUTES.v1.tenants.all,
		tenant
	);
	return res.data.tenant;
};

export const deleteTenant = async (id: string) => {
	await del(`${API_ROUTES.v1.tenants.all}/${encodeURIComponent(id)}`);
};

export const useTenants = () => useQuery([QueryKeys.tenants], getTenants);

export const useTenant = (tenantId?: string) =>
	useQuery([QueryKeys.tenants], getTenants, {
		enabled: !!tenantId,
		retry: false,
		select: tenantId
			? (tenants) => tenants.tenants?.find((t) => t.id === tenantId)
			: undefined
	});

export const useUpdateTenant = () => {
	const queryClient = useQueryClient();
	return useMutation<ITenant, unknown, ITenant>(
		async (value) => {
			const res = await put<ITenant, ITenant>(API_ROUTES.v1.tenants.all, value);
			return res.data;
		},
		{
			onSuccess: (tenant) => {
				queryClient.setQueryData([QueryKeys.tenant, tenant.id], {
					tenant
				});

				const allItemsRes = queryClient.getQueryData<ITenants>([
					QueryKeys.tenants
				]) || {
					tenants: [],
					total: 0
				};

				const newItems = allItemsRes.tenants?.map((t) =>
					t.id === tenant.id ? tenant : t
				);

				queryClient.setQueryData([QueryKeys.tenants], {
					tenants: newItems,
					total: allItemsRes.total
				});
			}
		}
	);
};
